import * as React from "react";
import {
  Title2,
  Body1,
  Body2,
  Button,
  Badge,
  makeStyles,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import { getEnvelopeStatus } from "../services/api";
import { Envelope, EnvelopeStatus } from "../types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  successBanner: {
    textAlign: "center",
    padding: "24px",
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  section: {
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  recipientItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

interface Props {
  envelope: Envelope;
  organizationId: string;
  onSendAnother: () => void;
}

const StatusConfirmation: React.FC<Props> = ({ envelope, organizationId, onSendAnother }) => {
  const styles = useStyles();
  const [status, setStatus] = React.useState<EnvelopeStatus | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const data = await getEnvelopeStatus(organizationId, envelope.id);
      setStatus(data);
    } catch {
      // Status polling is optional, don't block the UI
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = () => {
    // TODO: Replace with actual DD-Sign web app URL
    window.open(`http://localhost:5173/envelopes/${envelope.id}`, "_blank");
  };

  return (
    <div className={styles.container}>
      <div className={styles.successBanner}>
        <Title2 block>Sent!</Title2>
        <Body1>Your document has been sent for signing.</Body1>
      </div>

      <div className={styles.section}>
        <Body2 weight="semibold" block>
          {envelope.name}
        </Body2>
        <Badge appearance="filled" color="success">
          {envelope.status_label || envelope.status}
        </Badge>
      </div>

      {loading && <Spinner label="Loading status..." size="small" />}

      {status && status.recipients.length > 0 && (
        <div className={styles.section}>
          <Body2 weight="semibold" block style={{ marginBottom: "8px" }}>
            Recipients
          </Body2>
          {status.recipients.map((r) => (
            <div key={r.id} className={styles.recipientItem}>
              <div>
                <Body1 block>{r.name}</Body1>
                <Body2>{r.email}</Body2>
              </div>
              <Badge appearance="outline">{r.status_label}</Badge>
            </div>
          ))}
        </div>
      )}

      {status && (
        <div className={styles.section}>
          <Body2 weight="semibold" block>
            Completion
          </Body2>
          <Body1>{status.completion_percentage}%</Body1>
        </div>
      )}

      <Button appearance="primary" onClick={handleTrack}>
        Track in DD Sign
      </Button>
      <Button appearance="secondary" onClick={onSendAnother}>
        Send Another Document
      </Button>
    </div>
  );
};

export default StatusConfirmation;
